import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Search, Calendar, Building, CheckCircle, XCircle, RefreshCw, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, PenLine, X, Filter } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const UserServiceWorkOrder = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [signedFilter, setSignedFilter] = useState('');
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterProjects, setFilterProjects] = useState([]);
  const [showSignModal, setShowSignModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [signingInProgress, setSigningInProgress] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('access_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchFilterData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/projects/`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        setFilterProjects(Array.isArray(data) ? data : (data.results || []));
      }
    } catch (err) { console.error('Error fetching filter data:', err); }
  };

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);
      if (signedFilter) params.append('signed', signedFilter);
      if (selectedProjects.length > 0) params.append('projects', selectedProjects.join(','));
      if (searchTerm) params.append('search', searchTerm);
      if (sortField) params.append('ordering', sortDirection === 'desc' ? `-${sortField}` : sortField);

      const response = await fetch(`${API_BASE_URL}/api/service-work-orders/?${params}`, { headers: getHeaders() });
      if (!response.ok) throw new Error(`Failed to fetch orders (${response.status})`);

      const data = await response.json();
      if (data.results) {
        setOrders(data.results);
        setTotalCount(data.count || 0);
      } else {
        setOrders(Array.isArray(data) ? data : []);
        setTotalCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, signedFilter, selectedProjects, searchTerm, sortField, sortDirection]);

  const handleProjectToggle = (projectId) => {
    setSelectedProjects(prev => prev.includes(projectId) ? prev.filter(id => id !== projectId) : [...prev, projectId]);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(1);
  };

  const SortableHeader = ({ field, children }) => {
    const isActive = sortField === field;
    return (
      <th
        className="px-4 py-3 text-left text-[10px] font-bold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-steel-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          {isActive ? (
            sortDirection === 'asc' ? <ArrowUp className="w-3 h-3 text-brand-500" /> : <ArrowDown className="w-3 h-3 text-brand-500" />
          ) : (
            <ArrowUpDown className="w-3 h-3 text-steel-400" />
          )}
        </div>
      </th>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown')) setShowProjectDropdown(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => { fetchFilterData(); }, []);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { setCurrentPage(1); }, [signedFilter, selectedProjects, searchTerm]);

  const clearFilters = () => {
    setSelectedProjects([]);
    setSignedFilter('');
    setSearchTerm('');
  };

  const openSignModal = (order) => { setSelectedOrder(order); setShowSignModal(true); };
  const closeSignModal = () => { setShowSignModal(false); setSelectedOrder(null); };

  const handleConfirmSign = async () => {
    if (!selectedOrder) return;
    setSigningInProgress(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/service-work-orders/${selectedOrder.id}/sign/`, {
        method: 'POST', headers: getHeaders()
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to sign order');
      }
      fetchOrders();
      closeSignModal();
    } catch (err) {
      alert(err.message);
    } finally {
      setSigningInProgress(false);
    }
  };

  const hasActiveFilters = selectedProjects.length > 0 || signedFilter || searchTerm;
  const totalPages = Math.ceil(totalCount / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  const formatDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '-';
    return `${startDate} - ${endDate}`;
  };

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black text-slate-900">Construction Project Orders</h1>
            <p className="text-xs text-slate-500">View and sign your construction project orders</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1.5 bg-brand-100 text-brand-600 rounded-xl text-xs font-bold">
              {totalCount} Orders
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-brand-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-steel-100 flex items-center justify-center">
              <Filter className="h-4 w-4 text-slate-600" />
            </div>
            <h3 className="text-sm font-black text-slate-900 uppercase">Filters</h3>
          </div>

          <div className="flex flex-wrap gap-3 items-end">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-steel-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-9 pl-10 pr-4 border border-steel-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>
            </div>

            {/* Project filter */}
            <div className="relative min-w-[180px] filter-dropdown">
              <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Project</label>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowProjectDropdown(!showProjectDropdown); }}
                className={`w-full h-9 px-3 text-left flex justify-between items-center border rounded-xl text-sm font-medium ${
                  selectedProjects.length > 0 ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-slate-700'
                }`}
              >
                <span>{selectedProjects.length === 0 ? 'All Projects' : `${selectedProjects.length} selected`}</span>
                <span className="text-xs">▼</span>
              </button>
              {showProjectDropdown && (
                <div className="absolute top-full left-0 mt-1 min-w-[280px] bg-white border border-steel-200 rounded-xl shadow-lg z-20 flex flex-col max-h-[350px] overflow-hidden">
                  <div className="p-2 border-b border-steel-100">
                    <input
                      type="text"
                      placeholder="Search projects..."
                      value={projectSearchQuery}
                      onChange={(e) => setProjectSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full h-8 px-3 border border-steel-200 rounded-lg text-sm"
                    />
                  </div>
                  <div className="overflow-y-auto max-h-[280px]">
                    {filterProjects
                      .filter(p => !projectSearchQuery || p.name.toLowerCase().includes(projectSearchQuery.toLowerCase()))
                      .map(project => (
                        <label
                          key={project.id}
                          className={`flex items-center px-3 py-2 cursor-pointer border-b border-steel-50 hover:bg-steel-50 text-sm ${
                            selectedProjects.includes(project.id) ? 'bg-brand-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedProjects.includes(project.id)}
                            onChange={() => handleProjectToggle(project.id)}
                            className="mr-2 rounded text-brand-500 focus:ring-brand-500"
                          />
                          <span>{project.name}</span>
                        </label>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Signed filter */}
            <div>
              <label className="block text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-1">Signed</label>
              <select
                value={signedFilter}
                onChange={(e) => setSignedFilter(e.target.value)}
                className={`h-9 px-3 min-w-[120px] border rounded-xl text-sm font-medium focus:ring-2 focus:ring-brand-500 ${
                  signedFilter ? 'bg-brand-50 border-brand-200 text-brand-700' : 'bg-white border-steel-200 text-slate-700'
                }`}
              >
                <option value="">All</option>
                <option value="true">Signed</option>
                <option value="false">Not Signed</option>
              </select>
            </div>

            {/* Refresh button */}
            <button
              onClick={fetchOrders}
              className="h-9 w-9 flex items-center justify-center border border-steel-200 rounded-xl hover:bg-steel-50 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 text-slate-600 ${loading ? 'animate-spin' : ''}`} />
            </button>

            {/* Clear filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="h-9 px-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs font-bold hover:bg-red-100 transition-colors flex items-center gap-1"
              >
                <X size={14} /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-brand-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-10 text-center">
              <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Loading construction project orders...</p>
            </div>
          ) : error ? (
            <div className="p-10 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <XCircle className="w-6 h-6 text-red-500" />
              </div>
              <p className="text-red-600 text-sm font-medium mb-3">{error}</p>
              <button
                onClick={fetchOrders}
                className="h-9 px-4 bg-brand-500 text-white rounded-xl hover:bg-brand-600 font-bold text-xs uppercase tracking-wide transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : orders.length === 0 ? (
            <div className="p-10 text-center">
              <div className="w-12 h-12 bg-steel-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <FileText className="w-6 h-6 text-slate-400" />
              </div>
              <p className="text-sm font-bold text-slate-900 mb-1">No orders found</p>
              <p className="text-xs text-slate-400">
                {hasActiveFilters ? 'Try adjusting your filters' : 'You have no construction project orders assigned'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-steel-50 border-b border-steel-100">
                      <SortableHeader field="project__name">Project</SortableHeader>
                      <SortableHeader field="project__project_number">Project Number</SortableHeader>
                      <SortableHeader field="date_start">Date Range</SortableHeader>
                      <SortableHeader field="created_by__last_name">Created By</SortableHeader>
                      <SortableHeader field="created_at">Created At</SortableHeader>
                      <SortableHeader field="signed_by">Signed</SortableHeader>
                      <th className="px-4 py-3 text-right text-[10px] font-bold text-slate-700 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-steel-100">
                    {orders.map((order) => (
                      <tr key={order.id} className="hover:bg-steel-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building className="w-4 h-4 text-steel-400 mr-2" />
                            <span className="text-sm text-slate-700">{order.project_name || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-slate-700">{order.project_number || '-'}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center text-sm text-slate-500">
                            <Calendar className="w-4 h-4 mr-1" />
                            {order.date_start} - {order.date_end}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-slate-700">{order.created_by_name || '-'}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-slate-500">
                            {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {order.is_signed ? (
                            <div>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-md bg-emerald-50 text-emerald-600">
                                <CheckCircle className="w-3 h-3" /> Signed
                              </span>
                              <p className="text-[10px] text-slate-400 mt-0.5">
                                {order.signed_at ? new Date(order.signed_at).toLocaleDateString() : ''}
                              </p>
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded-md bg-amber-50 text-amber-600">
                              Not signed
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          {order.is_signed ? (
                            order.file ? (
                              <a
                                href={order.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center h-7 px-2 text-xs font-bold text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                              >
                                <FileText className="w-3 h-3 mr-1" /> View PDF
                              </a>
                            ) : (
                              <span className="text-xs text-slate-400">No file</span>
                            )
                          ) : (
                            <button
                              onClick={() => openSignModal(order)}
                              className="inline-flex items-center h-7 px-2 text-xs font-bold text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            >
                              <PenLine className="w-3 h-3 mr-1" /> Sign
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-3 bg-steel-50 border-t border-steel-100 flex items-center justify-between">
                <div className="text-xs text-slate-500">
                  Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={!hasPrevPage}
                    className="h-8 px-3 flex items-center gap-1 border border-steel-200 rounded-lg hover:bg-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" /> Prev
                  </button>
                  <span className="text-xs text-slate-600 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={!hasNextPage}
                    className="h-8 px-3 flex items-center gap-1 border border-steel-200 rounded-lg hover:bg-white text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Sign Modal */}
        {showSignModal && selectedOrder && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeSignModal} />
            <div className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
              {/* Modal Header */}
              <div className="px-4 py-3 border-b border-steel-100 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
                    <FileText className="h-4 w-4 text-brand-500" />
                  </div>
                  <h2 className="text-sm font-black text-slate-900 uppercase">
                    {selectedOrder.is_signed ? 'Order Details' : 'Sign Order'}
                  </h2>
                </div>
                <button onClick={closeSignModal} className="p-1.5 rounded-lg hover:bg-steel-100 text-slate-400">
                  <X size={18} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto flex-1">
                {/* Company Logo */}
                <div className="flex justify-center mb-6">
                  {selectedOrder.company_logo ? (
                    <img src={selectedOrder.company_logo} alt="Company Logo" className="h-16 object-contain" />
                  ) : (
                    <div className="text-xl font-black text-slate-700">{selectedOrder.company_name || 'Company'}</div>
                  )}
                </div>

                {/* Title */}
                <h3 className="text-center text-sm font-black text-slate-900 mb-6 uppercase tracking-wide">
                  Construction Project Order
                </h3>

                {/* Details Table */}
                <div className="border border-steel-200 rounded-xl overflow-hidden mb-6">
                  <table className="w-full">
                    <tbody>
                      {[
                        ['Project Manager', selectedOrder.project_manager],
                        ['Project Name & Address', <><div>{selectedOrder.project_name || '-'}</div>{selectedOrder.project_address && <div className="text-xs text-slate-500">{selectedOrder.project_address}</div>}</>],
                        ['Project Number', selectedOrder.project_number],
                        ['Customer', selectedOrder.project_customer],
                        ['Turbine Type', selectedOrder.turbine_type],
                        ['Number of Turbines', selectedOrder.number_of_turbines],
                        ['Type of Service Work', selectedOrder.type_service_work],
                        ['Planned Duration', formatDuration(selectedOrder.date_start, selectedOrder.date_end)],
                        ['Required Reports', <span className="whitespace-pre-line">{selectedOrder.required_reports || '-'}</span>],
                      ].map(([label, value], i) => (
                        <tr key={i} className={i < 8 ? 'border-b border-steel-200' : ''}>
                          <td className="px-4 py-3 bg-steel-50 font-bold text-xs text-slate-700 w-1/3 border-r border-steel-200 uppercase tracking-wide">
                            {label}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-900">{value || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Parties Section */}
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div className="bg-steel-50 rounded-xl p-4">
                    <h4 className="text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-2">Contracting Party</h4>
                    <div className="space-y-1">
                      <p className="text-sm font-bold text-slate-900">{selectedOrder.company_name || '-'}</p>
                      <p className="text-xs text-slate-500 whitespace-pre-line">{selectedOrder.company_address || '-'}</p>
                      <p className="text-xs text-slate-500">NIP: {selectedOrder.company_nip || '-'}</p>
                      <p className="text-xs text-slate-500">REGON: {selectedOrder.company_regon || '-'}</p>
                    </div>
                  </div>
                  <div className="bg-steel-50 rounded-xl p-4">
                    <h4 className="text-[10px] font-bold text-slate-700 uppercase tracking-widest mb-2">Contractor</h4>
                    <p className="text-sm font-bold text-slate-900">{selectedOrder.user_name || '-'}</p>
                  </div>
                </div>

                {/* Signature Status */}
                {selectedOrder.is_signed && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex items-center text-emerald-700">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      <span className="font-bold text-sm">Signed by {selectedOrder.signed_by_name}</span>
                    </div>
                    {selectedOrder.signed_at && (
                      <p className="text-xs text-emerald-600 mt-1">on {new Date(selectedOrder.signed_at).toLocaleString()}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-4 py-3 border-t border-steel-100 bg-steel-50 flex items-center justify-end gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={closeSignModal}
                  className="h-9 px-4 text-slate-600 rounded-xl hover:bg-steel-200 font-bold text-xs transition-colors"
                >
                  {selectedOrder.is_signed ? 'Close' : 'Cancel'}
                </button>
                {!selectedOrder.is_signed && (
                  <button
                    type="button"
                    onClick={handleConfirmSign}
                    disabled={signingInProgress}
                    className="h-9 px-4 flex items-center gap-2 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 font-bold text-xs uppercase tracking-wide transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                  >
                    {signingInProgress ? (
                      <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Signing...</>
                    ) : (
                      <><PenLine className="w-4 h-4" /> Sign Order</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserServiceWorkOrder;
